/**
 * DataFetchLogger - Structured logging for data fetches
 * 
 * Provides consistent logging schema for data fetches across the app:
 * - R2 bucket (music playlists)
 * - ATC CDN (manifests, stations, scenarios)
 * - Cache hits
 * - Fallback/mock data
 * 
 * Cross-cutting concern: kept at the boundary, does not pollute domain logic
 */

import { Logger } from '@/app/utils/logger';

const LOG_NAMESPACE = 'data-fetch';
const MAX_LOG_ENTRIES = 100;

/**
 * Data sources for fetch operations
 */
export type DataSource = 'r2' | 'atc-cdn' | 'cache' | 'memory-cache' | 'localStorage-cache' | 'mock' | 'fallback';

/**
 * Status of a fetch operation
 */
export type FetchStatus = 'success' | 'failure' | 'fallback';

/**
 * Structured log entry for data fetches
 */
export interface DataFetchLogEntry {
  /** ISO timestamp of the log entry */
  timestamp: string;
  /** Operation identifier, e.g., 'playlist:fetch', 'stations:fetch' */
  operation: string;
  /** Source of the data */
  source: DataSource;
  /** Status of the fetch */
  status: FetchStatus;
  /** Duration of the operation in milliseconds */
  durationMs: number;
  /** Whether data was served from cache */
  cacheHit?: boolean;
  /** Age of cached data in milliseconds */
  cacheAge?: number;
  /** ETag from response headers if available */
  etag?: string;
  /** Error message if status is failure */
  errorMessage?: string;
  /** Reason for fallback if status is fallback */
  fallbackReason?: string;
  /** Number of items returned (tracks, stations, scenarios) */
  itemCount?: number;
}

/**
 * Input type for logFetch (timestamp is auto-generated)
 */
export type DataFetchLogInput = Omit<DataFetchLogEntry, 'timestamp'>;

/**
 * DataFetchLogger class
 * 
 * Stores structured log entries in memory for diagnostics and debugging.
 * Uses the existing Logger utility for actual console output.
 */
export class DataFetchLogger {
  private static entries: DataFetchLogEntry[] = [];

  /**
   * Log a data fetch operation
   * 
   * @param input - Log entry data (timestamp will be auto-generated)
   */
  static logFetch(input: DataFetchLogInput): void {
    const entry: DataFetchLogEntry = {
      ...input,
      timestamp: new Date().toISOString(),
    };

    // Store entry in memory
    this.entries.push(entry);

    // Trim old entries if we exceed the limit
    if (this.entries.length > MAX_LOG_ENTRIES) {
      this.entries = this.entries.slice(-MAX_LOG_ENTRIES);
    }

    // Format and output to console via existing Logger
    const formattedMessage = this.formatLogEntry(entry);

    switch (entry.status) {
      case 'success':
        Logger.info(formattedMessage, LOG_NAMESPACE);
        break;
      case 'fallback':
        Logger.warn(formattedMessage, LOG_NAMESPACE);
        break;
      case 'failure':
        Logger.error(formattedMessage, LOG_NAMESPACE);
        break;
    }
  }

  /**
   * Get recent log entries
   * 
   * @param limit - Maximum number of entries to return (defaults to all)
   * @returns Array of log entries, newest first
   */
  static getRecentLogs(limit?: number): DataFetchLogEntry[] {
    const entries = [...this.entries].reverse();
    return limit ? entries.slice(0, limit) : entries;
  }

  /**
   * Format a log entry as a string for console output
   * 
   * Format: operation | source=X | status=X | duration=Xms | [optional fields]
   * 
   * @param entry - Log entry to format
   * @returns Formatted string
   */
  static formatLogEntry(entry: DataFetchLogEntry): string {
    const parts: string[] = [
      entry.operation,
      `source=${entry.source}`,
      `status=${entry.status}`,
      `duration=${entry.durationMs}ms`,
    ];

    if (entry.cacheHit !== undefined) {
      parts.push(`cacheHit=${entry.cacheHit}`);
    }

    if (entry.cacheAge !== undefined) {
      parts.push(`cacheAge=${entry.cacheAge}ms`);
    }

    if (entry.etag) {
      parts.push(`etag=${entry.etag}`);
    }

    if (entry.itemCount !== undefined) {
      parts.push(`items=${entry.itemCount}`);
    }

    if (entry.errorMessage) {
      parts.push(`error=${entry.errorMessage}`);
    }

    if (entry.fallbackReason) {
      parts.push(`reason=${entry.fallbackReason}`);
    }

    return parts.join(' | ');
  }

  /**
   * Clear all stored log entries
   */
  static clear(): void {
    this.entries = [];
  }

  /**
   * Get the count of stored entries
   */
  static getEntryCount(): number {
    return this.entries.length;
  }
}

/**
 * Helper function to measure the duration of an async operation
 * 
 * @param operation - Async function to measure
 * @returns Tuple of [result, durationMs]
 */
export async function measureFetchDuration<T>(
  operation: () => Promise<T>
): Promise<[T, number]> {
  const startTime = performance.now();
  const result = await operation();
  const durationMs = Math.round(performance.now() - startTime);
  return [result, durationMs];
}

/**
 * Helper function to measure and log a fetch operation
 * Wraps an async operation with automatic logging
 */
export async function loggedFetch<T>(
  operationName: string,
  source: DataSource,
  operation: () => Promise<T>,
  options: {
    getItemCount?: (result: T) => number;
    cacheHit?: boolean;
    cacheAge?: number;
    etag?: string;
  } = {}
): Promise<T> {
  const startTime = performance.now();

  try {
    const result = await operation();
    const durationMs = Math.round(performance.now() - startTime);

    DataFetchLogger.logFetch({
      operation: operationName,
      source,
      status: 'success',
      durationMs,
      cacheHit: options.cacheHit,
      cacheAge: options.cacheAge,
      etag: options.etag,
      itemCount: options.getItemCount?.(result),
    });

    return result;
  } catch (error) {
    const durationMs = Math.round(performance.now() - startTime);
    const errorMessage = error instanceof Error ? error.message : String(error);

    DataFetchLogger.logFetch({
      operation: operationName,
      source,
      status: 'failure',
      durationMs,
      errorMessage,
    });

    throw error;
  }
}
